import { useEffect, useState } from "react";
import { Badge, Box, Button, Icon, Popover, PopoverPopup, PopoverTrigger, Text } from "@salestrak/ui";
import { useAuth } from "#app/lib/auth-context";
import { FEATURE_FLAG_DEFINITION_LIST, useFeatureFlags } from "#app/hooks/use-feature-flags";

function minutesLeft(expiresAt: string): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).valueOf() - Date.now()) / 60_000));
}

export function ImpersonationBanner() {
  const { impersonation, stopImpersonation } = useAuth();
  const [minutes, setMinutes] = useState(() => (impersonation ? minutesLeft(impersonation.expiresAt) : 0));
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!impersonation) return;
    setMinutes(minutesLeft(impersonation.expiresAt));
    const timer = setInterval(() => setMinutes(minutesLeft(impersonation.expiresAt)), 15_000);
    return () => clearInterval(timer);
  }, [impersonation]);

  if (!impersonation) return null;

  const { user, workspace } = impersonation;
  const who = user.name || user.email || "a member";

  return (
    // Stacks above the z-50 dialog layer so the session stays visible, and
    // exitable, while a full-window dialog is open. Black ink on the warning
    // fill reads in both themes (amber in light, yellow in dark).
    <Box
      role="status"
      direction="row"
      align="center"
      gap="md"
      paddingX="md"
      paddingY="xs"
      background="warning"
      position="relative"
      zIndex={60}
      shrink={false}
      className="text-black"
    >
      <Box direction="row" align="center" gap="xs" shrink={false}>
        <Icon name="shield" size="sm" />
        <Text size="xs" weight="bold" caps>
          Impersonating
        </Text>
      </Box>
      <Box direction="row" align="baseline" gap="xs" fill="flex" minWidth={0}>
        <Text size="sm" weight="bold" truncate={1}>
          {who}
        </Text>
        {user.name && user.email ? (
          <Text size="sm" truncate={1} className="text-black/70">
            {user.email}
          </Text>
        ) : null}
        <Text size="sm" className="text-black/70">
          in
        </Text>
        <Text size="sm" weight="bold" truncate={1}>
          {workspace.name}
        </Text>
      </Box>
      <EnabledFlags />
      <Text size="xs" weight="semibold" mono tabular>
        {`${minutes}m left`}
      </Text>
      <Button
        size="sm"
        variant="outline"
        disabled={exiting}
        onClick={() => {
          setExiting(true);
          void stopImpersonation();
        }}
      >
        <Icon name="log-out" size="sm" />
        {exiting ? "Exiting…" : "Exit"}
      </Button>
    </Box>
  );
}

/** The flags resolved for the impersonated member in this workspace — what
 *  they see, so the superuser can tell which variant of the app they're in. */
function EnabledFlags() {
  const { ready, hasFlag } = useFeatureFlags();
  if (!ready) return null;
  const enabled = FEATURE_FLAG_DEFINITION_LIST.filter((flag) => hasFlag(flag.key));
  const label = `${enabled.length} feature ${enabled.length === 1 ? "flag" : "flags"}`;

  return (
    <Popover>
      <PopoverTrigger render={<Button size="sm" variant="outline" />}>
        <Icon name="flag" size="sm" />
        {label}
        <Icon name="chevron-down" size="sm" />
      </PopoverTrigger>
      <PopoverPopup side="bottom" align="end" padding="none">
        <Box width={320}>
          <Box direction="row" align="center" gap="sm" paddingX="md" paddingY="sm" border="bottom">
            <Icon name="flag" size="sm" color="muted" />
            <Box fill="flex" minWidth={0} gap="2xs">
              <Text size="sm" weight="semibold">
                Feature flags
              </Text>
              <Text size="xs" variant="muted">
                Enabled for this member
              </Text>
            </Box>
            <Badge size="sm" tone="soft" color="brand">
              {enabled.length}
            </Badge>
          </Box>
          {enabled.length === 0 ? (
            <Box align="center" gap="xs" paddingX="md" paddingY="lg">
              <Icon name="flag" size="md" color="muted" />
              <Text size="sm" variant="muted">
                No feature flags enabled
              </Text>
            </Box>
          ) : (
            <Box paddingY="xs" maxHeight={360} overflow="auto">
              {enabled.map((flag) => (
                <Box key={flag.key} direction="row" align="start" gap="sm" paddingX="md" paddingY="sm">
                  <Icon name="check-circle" size="sm" color="success" />
                  <Box fill="flex" minWidth={0} gap="2xs">
                    <Text size="sm" weight="medium" truncate={1}>
                      {flag.title}
                    </Text>
                    {flag.description ? (
                      <Text size="xs" variant="muted">
                        {flag.description}
                      </Text>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </PopoverPopup>
    </Popover>
  );
}
